/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pagination } from 'antd';
import { FileSpreadsheet, FilterX, History, Info } from 'lucide-react';
import moment from 'moment';
import get from 'lodash/get';
import ExcelJS from 'exceljs';
import axiosConfig from '../redux/sparesDashboard/axiosConfig';
import { Button } from '@crc-evans/ui-components';
import SpareOrderHistoryGrid from './SpareOrderHistoryGrid';
import { useHeatStyleColumnFacets } from './technical/heatStyleColumnFacets';
import './DSR/DownTimeModal.css';
import './SpareOrderHistoryModal.css';
import './DPR/DPRSettingsSparesTab.css';
import '../pages/technical/HeatProfilePage.css';

const ORDER_HISTORY_HEAT_FACET_KEYS = [
  'equipmentType',
  'sapKitNo',
  'sapNumber',
  'description',
  '__createdAtDisplay',
  '__typeDisplay',
  '__orderedByDisplay',
];

const ORDER_HISTORY_HEAT_FILTERS_EMPTY = ORDER_HISTORY_HEAT_FACET_KEYS.reduce((acc, k) => {
  acc[k] = '';
  return acc;
}, {});

const LOCAL_COL_FILTERS_INITIAL = {
  createdAt: '',
  quantity: '',
  __typeDisplay: '',
  orderedBy: '',
};

const FETCH_PAGE_SIZE = 10000;

const normalizeOrderRow = (record, index) => {
  if (!record || typeof record !== 'object') {
    return { id: `row-${index}` };
  }
  return {
    ...record,
    id: record.id ?? record.orderId ?? record.spareOrderId ?? `row-${index}`,
    createdAt: record.createdAt ?? record.orderedOn ?? record.orderDate ?? record.created_at,
    sapNumber: record.sapNumber ?? record.partSapNumber ?? record.sapNo ?? record.sap_number,
    description: record.description ?? record.partDescription ?? record.spareDescription,
    quantity: record.quantity ?? record.qty ?? record.orderQuantity,
    orderTypeLabel: record.orderTypeLabel ?? record.orderType ?? record.type,
    urgent: Boolean(record.urgent ?? record.isUrgent),
    orderedBy: record.orderedBy ?? record.orderedByName ?? record.userName ?? record.createdBy,
    orderedByEmail: record.orderedByEmail ?? record.userEmail ?? record.email,
    equipmentType: record.equipmentType ?? record.equipment ?? record.pihEquipmentType,
    equipmentSerialNumber:
      record.equipmentSerialNumber ??
      record.serialNumber ??
      record.assignedToSerialNumber ??
      record.equipmentSerialNo,
    sapKitNo: record.sapKitNo ?? record.sapSparesKitNumber ?? record.sapKitNumber,
  };
};

const parseOrdersResponse = (res) => {
  const payload = get(res, 'data.data', get(res, 'data', {}));
  if (Array.isArray(payload)) {
    return { rows: payload, totalCount: payload.length };
  }
  const rows = payload.rows ?? payload.items ?? payload.orders ?? payload.data ?? [];
  const totalCount =
    Number(payload.totalCount ?? payload.total ?? payload.count ?? rows.length) || 0;
  return {
    rows: Array.isArray(rows) ? rows : [],
    totalCount,
  };
};

/** API timestamps are stored/transmitted in UTC — display date + time in UTC. */
export const formatSpareOrderDate = (value) => {
  if (!value) return '—';
  const m = moment.utc(value);
  if (!m.isValid()) return '—';
  return m.format('DD-MMM-YYYY, HH:mm');
};

export const formatSpareOrderOrderedBy = (row) => {
  const name = (row?.orderedBy || '').trim();
  const email = (row?.orderedByEmail || '').trim();
  if (name && email) {
    if (name.toLowerCase() === email.toLowerCase()) return email;
    return `${name} (${email})`;
  }
  return name || email || '—';
};

const enrichOrderRowForFacets = (row) => ({
  ...row,
  __createdAtDisplay: formatSpareOrderDate(row.createdAt),
  __typeDisplay: row.urgent ? 'Urgent' : row.orderTypeLabel || 'Normal',
  __orderedByDisplay: formatSpareOrderOrderedBy(row),
});

const SpareOrderHistoryModal = ({ open, projectId, onClose }) => {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const [equipmentTypeValue, setEquipmentTypeValue] = useState('');
  const [sapKitNoValue, setSapKitNoValue] = useState('');
  const [sapNumberValue, setSapNumberValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [heatColumnFilters, setHeatColumnFilters] = useState(() => ({
    ...ORDER_HISTORY_HEAT_FILTERS_EMPTY,
  }));
  const [localColFilters, setLocalColFilters] = useState(() => ({ ...LOCAL_COL_FILTERS_INITIAL }));

  const facetSource = useMemo(
    () => allRows.map(enrichOrderRowForFacets),
    [allRows]
  );

  const facetListRef = useRef([]);
  facetListRef.current = facetSource;

  const fetchFacetRows = useCallback(async () => {
    const list = facetListRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const handleHeatFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const {
    applyFacetClientFilter,
    renderTextColumnFilterTrigger,
    resetColumnFilterPanel,
    isTextColumnFilterActive,
  } = useHeatStyleColumnFacets({
    enabled: open && Boolean(projectId),
    filterableColumns: ORDER_HISTORY_HEAT_FACET_KEYS,
    listCount: facetSource.length,
    projectList: facetSource,
    searchCriteria: {},
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleHeatFilterChange,
    fetchFacetRows,
  });

  useEffect(() => {
    setPage(1);
  }, [projectId]);

  useEffect(() => {
    if (!open) {
      setEquipmentTypeValue('');
      setSapKitNoValue('');
      setSapNumberValue('');
      setDescriptionValue('');
      setHeatColumnFilters({ ...ORDER_HISTORY_HEAT_FILTERS_EMPTY });
      setLocalColFilters({ ...LOCAL_COL_FILTERS_INITIAL });
      setAllRows([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          projectId: String(projectId),
          pageNumber: '1',
          pageSize: String(FETCH_PAGE_SIZE),
        });
        const res = await axiosConfig.get(`/project-spare-orders?${params.toString()}`);
        if (cancelled) return;
        const { rows: rawRows } = parseOrdersResponse(res);
        setAllRows(rawRows.map(normalizeOrderRow));
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load spare order history', err);
          setAllRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const handleTextColumnFilterChange = useCallback((_filterType, value, setter) => {
    setter(value);
    setPage(1);
  }, []);

  const handleLocalColFilterChange = useCallback((field, value) => {
    setLocalColFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }, []);

  const toggleSort = useCallback((field) => {
    setSortKey((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  const filteredRows = useMemo(() => {
    let rows = [...facetSource];
    const q = (s) => (s || '').trim().toLowerCase();
    const includes = (val, term) => String(val ?? '').toLowerCase().includes(term);

    if (q(equipmentTypeValue)) {
      const t = q(equipmentTypeValue);
      rows = rows.filter((r) => includes(r.equipmentType, t));
    }
    if (q(sapKitNoValue)) {
      const t = q(sapKitNoValue);
      rows = rows.filter((r) => includes(r.sapKitNo, t));
    }
    if (q(sapNumberValue)) {
      const t = q(sapNumberValue);
      rows = rows.filter((r) => includes(r.sapNumber, t));
    }
    if (q(descriptionValue)) {
      const t = q(descriptionValue);
      rows = rows.filter((r) => includes(r.description, t));
    }

    ORDER_HISTORY_HEAT_FACET_KEYS.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim();
      if (!raw) return;
      const t = raw.toLowerCase();
      rows = rows.filter((r) => String(get(r, key) ?? '').toLowerCase().includes(t));
    });

    rows = applyFacetClientFilter(rows);

    const f = localColFilters;
    if (q(f.createdAt)) {
      const t = q(f.createdAt);
      rows = rows.filter((r) => includes(r.__createdAtDisplay, t));
    }
    if (q(f.quantity)) {
      const t = q(f.quantity);
      rows = rows.filter((r) => includes(r.quantity, t));
    }
    if (q(f.__typeDisplay)) {
      const t = q(f.__typeDisplay);
      rows = rows.filter((r) => includes(r.__typeDisplay, t));
    }
    if (q(f.orderedBy)) {
      const t = q(f.orderedBy);
      rows = rows.filter((r) => includes(r.__orderedByDisplay, t));
    }

    return rows;
  }, [
    facetSource,
    equipmentTypeValue,
    sapKitNoValue,
    sapNumberValue,
    descriptionValue,
    heatColumnFilters,
    applyFacetClientFilter,
    localColFilters,
  ]);

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    const dir = sortDir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      if (sortKey === 'createdAt') {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return (ta - tb) * dir;
      }
      if (sortKey === 'quantity') {
        const na = Number(a.quantity);
        const nb = Number(b.quantity);
        return ((Number.isNaN(na) ? 0 : na) - (Number.isNaN(nb) ? 0 : nb)) * dir;
      }
      if (sortKey === 'orderedBy') {
        return a.__orderedByDisplay.localeCompare(b.__orderedByDisplay) * dir;
      }
      if (sortKey === 'orderTypeLabel') {
        return a.__typeDisplay.localeCompare(b.__typeDisplay) * dir;
      }
      const va = String(a[sortKey] ?? '');
      const vb = String(b[sortKey] ?? '');
      return va.localeCompare(vb) * dir;
    });
    return list;
  }, [filteredRows, sortKey, sortDir]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const totalFiltered = sortedRows.length;

  const hasAnyColumnFilter = useMemo(() => {
    const q = (s) => String(s ?? '').trim();
    if (
      q(equipmentTypeValue) ||
      q(sapKitNoValue) ||
      q(sapNumberValue) ||
      q(descriptionValue)
    ) {
      return true;
    }
    if (Object.values(localColFilters).some((v) => q(v))) return true;
    return ORDER_HISTORY_HEAT_FACET_KEYS.some((key) => isTextColumnFilterActive(key));
  }, [
    equipmentTypeValue,
    sapKitNoValue,
    sapNumberValue,
    descriptionValue,
    localColFilters,
    isTextColumnFilterActive,
  ]);

  const handleClearAllFilters = useCallback(() => {
    ORDER_HISTORY_HEAT_FACET_KEYS.forEach((key) => resetColumnFilterPanel(key));
    setEquipmentTypeValue('');
    setSapKitNoValue('');
    setSapNumberValue('');
    setDescriptionValue('');
    setHeatColumnFilters({ ...ORDER_HISTORY_HEAT_FILTERS_EMPTY });
    setLocalColFilters({ ...LOCAL_COL_FILTERS_INITIAL });
    setPage(1);
  }, [resetColumnFilterPanel]);

  const handleExportExcel = useCallback(async () => {
    if (!sortedRows.length) return;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Spare order history');
    sheet.columns = [
      { header: 'Ordered on', key: 'orderedOn', width: 14 },
      { header: 'Equipment type', key: 'equipmentType', width: 22 },
      { header: 'SAP kit', key: 'sapKitNo', width: 16 },
      { header: 'SAP number', key: 'sapNumber', width: 18 },
      { header: 'Description', key: 'description', width: 36 },
      { header: 'Qty', key: 'quantity', width: 8 },
      { header: 'Type', key: 'type', width: 12 },
      { header: 'Ordered by', key: 'orderedBy', width: 36 },
    ];
    const headerRow = sheet.getRow(1);
    headerRow.height = 28;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
    });
    sortedRows.forEach((row) => {
      sheet.addRow({
        orderedOn: formatSpareOrderDate(row.createdAt),
        equipmentType: row.equipmentType ?? '',
        sapKitNo: row.sapKitNo ?? '',
        sapNumber: row.sapNumber ?? '',
        description: row.description ?? '',
        quantity: row.quantity ?? '',
        type: row.__typeDisplay ?? (row.urgent ? 'Urgent' : row.orderTypeLabel || 'Normal'),
        orderedBy: formatSpareOrderOrderedBy(row),
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'spare-order-history.xlsx';
    link.click();
    URL.revokeObjectURL(url);
  }, [sortedRows]);

  const modalTitle = (
    <div className="spare-order-history-modal-head">
      <span className="spare-order-history-modal-title">
        <span className="spare-order-history-modal-title-icon" aria-hidden>
          <History size={18} />
        </span>
        Spare parts order history
      </span>
      <div className="spare-order-history-modal-head-actions">
        {hasAnyColumnFilter ? (
          <Button
            label="Clear filters"
            variant="tertiary"
            size="medium"
            icon={FilterX}
            onClick={handleClearAllFilters}
            disabled={loading}
          />
        ) : null}
        <Button
          label="Export Excel"
          variant="primary"
          size="medium"
          icon={FileSpreadsheet}
          onClick={handleExportExcel}
          disabled={loading || sortedRows.length === 0}
        />
      </div>
    </div>
  );

  return (
    <Modal
      width={1280}
      style={{ maxWidth: '96vw' }}
      className="spare-order-history-modal modern-dsr-dt-add-modal dpr-settings-spares-form-modal"
      wrapClassName="dpr-settings-spares-ant-modal-wrap spare-order-history-modal-wrap"
      title={modalTitle}
      centered
      visible={open}
      closable={false}
      maskClosable
      destroyOnClose
      onCancel={onClose}
      footer={
        <div className="modern-dsr-dt-add-modal__foot" role="group" aria-label="Order history actions">
          <Button label="Close" variant="primary" size="medium" onClick={onClose} />
        </div>
      }
    >
      <SpareOrderHistoryGrid
        rows={paginatedRows}
        isLoading={loading}
        current={page}
        pageSize={pageSize}
        sortKey={sortKey}
        sortDir={sortDir}
        onToggleSort={toggleSort}
        renderTextColumnFilterTrigger={renderTextColumnFilterTrigger}
        isTextColumnFilterActive={isTextColumnFilterActive}
        onTextColumnFilterChange={handleTextColumnFilterChange}
        equipmentTypeValue={equipmentTypeValue}
        setEquipmentTypeValue={setEquipmentTypeValue}
        sapKitNoValue={sapKitNoValue}
        setSapKitNoValue={setSapKitNoValue}
        sapNumberValue={sapNumberValue}
        setSapNumberValue={setSapNumberValue}
        descriptionValue={descriptionValue}
        setDescriptionValue={setDescriptionValue}
        localColFilters={localColFilters}
        onLocalColFilterChange={handleLocalColFilterChange}
        formatOrderDate={formatSpareOrderDate}
        formatOrderedByCell={formatSpareOrderOrderedBy}
      />
      {(totalFiltered > 0 || allRows.length > 0) && (
        <div className="spare-order-history-modal-pagination dpr-settings-spares-pagination">
          <Pagination
            className="heat-profile-pagination"
            current={page}
            pageSize={pageSize}
            total={totalFiltered}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={(nextPage, nextSize) => {
              setPage(nextPage);
              if (nextSize != null) setPageSize(nextSize);
            }}
            showTotal={(total, range) => (
              <span className="spare-order-history-pagination-total-wrap">
                <span>{`${range[0]} - ${range[1]} of ${total} orders`}</span>
                <span className="spare-order-history-utc-note" role="note">
                  <Info className="spare-order-history-utc-info-icon" size={16} strokeWidth={2} aria-hidden />
                  <span>Ordered on date and time are shown in UTC.</span>
                </span>
              </span>
            )}
          />
        </div>
      )}
    </Modal>
  );
};

export default SpareOrderHistoryModal;
